import React, { useState } from 'react';
import { getServiceRequests } from '../../services/serviceRequestApi';

const STATUS_BADGE = {
  'Open': 'bg-info',
  'In Progress': 'bg-primary',
  'Completed': 'bg-success',
  'Cancelled': 'bg-secondary',
};

const PRIORITY_BADGE = {
  Low: 'bg-secondary',
  Normal: 'bg-info',
  High: 'bg-warning text-dark',
  Emergency: 'bg-danger',
};

const MyRequests = () => {
  const [accountNumber, setAccountNumber] = useState('');
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!accountNumber.trim()) return;

    setIsLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const result = await getServiceRequests(accountNumber.trim());
      if (result.success) {
        setRequests(result.requests);
      } else {
        setError(result.error);
        setRequests([]);
      }
    } catch (err) {
      setError(err.message);
      setRequests([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  return (
    <>
      {/* Search bar */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <form onSubmit={handleSearch} className="row g-2 align-items-end">
            <div className="col-md-6">
              <label className="form-label fw-semibold mb-1">Account Number</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. TSE-9012-3456"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <button type="submit" className="btn btn-tse-primary w-100" disabled={isLoading || !accountNumber.trim()}>
                {isLoading ? (
                  <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Searching...</>
                ) : (
                  <><i className="bi bi-search me-2"></i>Search</>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger border-0 d-flex align-items-center">
          <i className="bi bi-exclamation-triangle me-2"></i>{error}
        </div>
      )}

      {/* Results */}
      {hasSearched && !isLoading && !error && (
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-light border-0">
            <h5 className="card-title mb-0">
              <i className="bi bi-list-check me-2 text-tse-primary"></i>
              Service Requests
              <span className="badge bg-secondary ms-2">{requests.length}</span>
            </h5>
          </div>

          {requests.length === 0 ? (
            <div className="card-body text-center py-5">
              <i className="bi bi-inbox display-4 text-muted mb-3 d-block"></i>
              <p className="text-muted">No service requests found for this account.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Request ID</th>
                    <th>Category</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th>Submitted</th>
                    <th>Service Address</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((req) => (
                    <tr key={req.id}>
                      <td className="fw-semibold">{req.id}</td>
                      <td>{req.category}</td>
                      <td><span className={`badge ${PRIORITY_BADGE[req.priority] || 'bg-secondary'}`}>{req.priority}</span></td>
                      <td><span className={`badge ${STATUS_BADGE[req.status] || 'bg-secondary'}`}>{req.status}</span></td>
                      <td>{formatDate(req.createdAt)}</td>
                      <td className="small">
                        {req.serviceAddress.street}<br />
                        <span className="text-muted">{req.serviceAddress.city}, {req.serviceAddress.state} {req.serviceAddress.postalCode}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Initial state */}
      {!hasSearched && (
        <div className="card border-0 shadow-sm">
          <div className="card-body text-center py-5">
            <i className="bi bi-search display-4 text-muted mb-3 d-block"></i>
            <h5 className="text-muted">Enter an account number to view service requests</h5>
            <p className="text-muted small">Search by account number to see all submitted requests and their current status.</p>
          </div>
        </div>
      )}
    </>
  );
};

export default MyRequests;
